import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, TrendingUp } from "lucide-react";
import { api, money0, formatApiError } from "../../lib/api";
import { TIER_META } from "../../lib/tiers";

const Big = ({ label, value, tid, accent }) => (
  <div className="lux-card p-5">
    <p className="eyebrow !text-slate-500">{label}</p>
    <p data-testid={tid} className={`font-mono-lux text-xl md:text-2xl mt-2 ${accent ? "text-[#38BDF8]" : ""}`}>{value}</p>
  </div>
);

const LineRow = ({ l }) => (
  <tr data-testid={`scale-line-${l.tier}`} className="border-b border-white/5 hover:bg-[#283454]">
    <td className="p-4"><p>{TIER_META[l.tier]?.short}</p><p className="text-xs text-slate-500">{l.weight_pct}% · sample {l.sample_size}</p></td>
    <td className="font-mono-lux">{l.subscribers.toLocaleString()}</td>
    <td className="font-mono-lux">{money0(l.avg_fee)} / {money0(l.avg_trip)}</td>
    <td className="font-mono-lux">{money0(l.monthly_revenue)}</td>
    <td className="font-mono-lux text-[#38BDF8]">{money0(l.owner_net)}</td>
    <td className="font-mono-lux">{money0(l.charity)}</td>
    <td className="font-mono-lux">{l.trips_per_month}<span className="text-xs text-slate-500"> · every {l.days_between_trips}d</span></td>
    <td className="font-mono-lux pr-4">{l.avg_wait_months} / {l.back_of_line_months} mo</td>
  </tr>
);

export const ScaleDemo = () => {
  const [total, setTotal] = useState(100000);
  const [f, setF] = useState(null);
  const run = useCallback((n) => api.get(`/admin/scale/forecast?total=${n}`).then((r) => setF(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { run(100000); }, [run]);
  const m = f?.monthly, y = f?.yearly;

  return (
    <div className="space-y-6" data-testid="scale-demo">
      <div className="lux-card p-6 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[240px]">
          <p className="eyebrow flex items-center gap-2"><TrendingUp className="w-4 h-4" />Admin-only forecast</p>
          <p className="text-sm text-slate-400 mt-2">Projects your economics at scale using the real plan / premium / autopay mix of the members in each line (base prices until members join). Public pages show real numbers only.</p>
        </div>
        <label className="text-xs text-slate-400">Subscribers
          <input data-testid="scale-total-input" type="number" min={100} value={total} onChange={(e) => setTotal(Number(e.target.value))}
            className="block mt-1 w-40 bg-[#0B0F19] border border-white/10 rounded-lg px-3 py-2 font-mono-lux text-white" />
        </label>
        <button data-testid="scale-run-btn" onClick={() => run(total)} className="btn-gold px-5 py-2.5 text-sm">Run forecast</button>
      </div>
      {f && <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Big label="Monthly revenue" value={money0(m.revenue)} tid="scale-monthly-revenue" />
          <Big label="Your 15% (net of scholarships)" value={money0(m.owner_net)} tid="scale-owner-net" accent />
          <Big label="Scholarships / month" value={money0(m.charity)} tid="scale-charity" />
          <Big label="Trips / month" value={Math.round(m.trips).toLocaleString()} tid="scale-trips" />
          <Big label="Yearly revenue" value={money0(y.revenue)} tid="scale-yearly-revenue" />
          <Big label="Your yearly net" value={money0(y.owner_net)} tid="scale-yearly-owner" accent />
          <Big label="Trips / year" value={Math.round(y.trips).toLocaleString()} tid="scale-yearly-trips" />
          <Big label="Founder discount cost / mo" value={money0(f.founder_discount_cost)} tid="scale-founder-cost" />
          <Big label={`Partner fees / mo (${f.partner_fee_pct}%)`} value={money0(m.partner_fees)} tid="scale-partner-fees" />
          <Big label={`Partner commission / mo (${f.partner_commission_pct}%)`} value={money0(m.partner_commission)} tid="scale-partner-commission" />
          <Big label={`Scholarships from commission / mo (${f.partner_charity_pct}%)`} value={money0(m.partner_charity)} tid="scale-partner-charity" accent />
          <Big label="Total scholarships / year" value={money0(y.charity + y.partner_charity)} tid="scale-total-charity-year" accent />
        </div>
        <div className="lux-card overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]" data-testid="scale-lines-table">
            <thead className="text-[10px] font-mono-lux tracking-[0.2em] uppercase text-slate-500 text-left">
              <tr className="border-b border-white/5"><th className="p-4">Line</th><th>Subscribers</th><th>Avg fee / trip</th><th>Revenue / mo</th><th>Your net / mo</th><th>Scholarships / mo</th><th>Trips / mo</th><th className="pr-4">Wait avg / back</th></tr>
            </thead>
            <tbody>{f.lines.map((l) => <LineRow key={l.tier} l={l} />)}</tbody>
          </table>
        </div>
        <p data-testid="scale-wait-note" className="text-xs text-slate-500 leading-relaxed max-w-3xl">
          More subscribers means more trips per month, not shorter waits. The wait at the back of a line is roughly trip cost ÷ (monthly fee × {100 - f.owner_pct}%), so it stays the same at any size unless new members keep joining behind you. Founder discount cost assumes the first {f.founder_limit} members get {f.founder_discount_pct}% off the average fee. Forecast only, not a promise. Partner figures assume every trip is booked through a partner (after each partner's free first booking).
        </p>
      </>}
    </div>
  );
};
